/**
 * Screener page theme-safe styles and two small display tiles (Screener Phase 3, 2026-10-28): moved verbatim out
 * of src/pages/ScreenView.tsx so the page's section components share one copy.
 */
import React from "react";
import type { Tone } from "@lib/screenerTypes";

/* ===========================
   Theme-safe helpers
   =========================== */
export const ui = {
  pageBg: "var(--bg-primary)",
  cardBg: "var(--card-bg)",
  cardBorder: "var(--card-border)",
  textPrimary: "var(--text-primary)",
  textSecondary: "var(--text-secondary)",
  textMuted: "var(--text-muted)",
  shadow: "var(--shadow-soft)",
  rowAlt: "rgba(148, 163, 184, 0.08)",
  rowHeader: "rgba(148, 163, 184, 0.12)",
  inputBg: "var(--card-bg)",
  inputBorder: "var(--card-border)",
  focusRing: "rgba(59, 130, 246, 0.28)",
};

export const toneStyles: Record<Tone, React.CSSProperties> = {
  neutral: {
    backgroundColor: "rgba(148, 163, 184, 0.14)",
    border: "1px solid rgba(148, 163, 184, 0.24)",
    color: ui.textPrimary,
  },
  info: {
    backgroundColor: "rgba(59, 130, 246, 0.14)",
    border: "1px solid rgba(59, 130, 246, 0.28)",
    color: ui.textPrimary,
  },
  success: {
    backgroundColor: "rgba(16, 185, 129, 0.14)",
    border: "1px solid rgba(16, 185, 129, 0.28)",
    color: ui.textPrimary,
  },
  warning: {
    backgroundColor: "rgba(245, 158, 11, 0.14)",
    border: "1px solid rgba(245, 158, 11, 0.28)",
    color: ui.textPrimary,
  },
  danger: {
    backgroundColor: "rgba(239, 68, 68, 0.14)",
    border: "1px solid rgba(239, 68, 68, 0.28)",
    color: ui.textPrimary,
  },
};

export const cardStyle: React.CSSProperties = {
  backgroundColor: ui.cardBg,
  border: `1px solid ${ui.cardBorder}`,
  color: ui.textPrimary,
  boxShadow: ui.shadow,
};

export const mutedText: React.CSSProperties = {
  color: ui.textMuted,
};

export const secondaryText: React.CSSProperties = {
  color: ui.textSecondary,
};

export function badgeStyle(tone: Tone): React.CSSProperties {
  return {
    ...toneStyles[tone],
    borderRadius: 999,
    padding: "3px 10px",
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: "nowrap",
  };
}

export function inputStyle(disabled = false): React.CSSProperties {
  return {
    width: "100%",
    padding: "0.5rem 0.75rem",
    border: `1px solid ${ui.inputBorder}`,
    borderRadius: "0.75rem",
    backgroundColor: ui.inputBg,
    color: ui.textPrimary,
    opacity: disabled ? 0.65 : 1,
    outline: "none",
    boxShadow: "none",
  };
}

export function progressTrackStyle(): React.CSSProperties {
  return {
    height: 10,
    borderRadius: 999,
    border: `1px solid ${ui.cardBorder}`,
    backgroundColor: "rgba(148, 163, 184, 0.08)",
    overflow: "hidden",
  };
}

export function progressFillStyle(widthPct: number, tone: Tone): React.CSSProperties {
  let background = "linear-gradient(90deg, rgba(148,163,184,0.9) 0%, rgba(148,163,184,0.72) 100%)";
  if (tone === "success") {
    background = "linear-gradient(90deg, rgba(16,185,129,0.95) 0%, rgba(52,211,153,0.85) 100%)";
  } else if (tone === "info") {
    background = "linear-gradient(90deg, rgba(59,130,246,0.95) 0%, rgba(96,165,250,0.85) 100%)";
  } else if (tone === "warning") {
    background = "linear-gradient(90deg, rgba(245,158,11,0.95) 0%, rgba(251,191,36,0.9) 100%)";
  } else if (tone === "danger") {
    background = "linear-gradient(90deg, rgba(239,68,68,0.95) 0%, rgba(248,113,113,0.85) 100%)";
  }

  return {
    width: `${Math.min(100, Math.max(0, widthPct))}%`,
    height: "100%",
    borderRadius: 999,
    background,
  };
}


// normalizeScoreDriverArray / scoreDriverLabel / scoreDriverValue /
// scoreDriverChipText removed 2026-08-11 with the drawer's ML feature-attribution
// chips, their only callers in this file and the codebase. mlTopPositiveDrivers /
// mlTopNegativeDrivers remain in the payload and the printable export.

export function AnalyticsMetric({
  label,
  value,
  subtext,
}: {
  label: string;
  value: string;
  subtext?: string | null;
}) {
  return (
    <div
      className="rounded-2xl border p-3"
      style={{
        backgroundColor: "rgba(148, 163, 184, 0.06)",
        borderColor: ui.cardBorder,
      }}
    >
      <div className="text-[11px] uppercase tracking-[0.14em]" style={mutedText}>
        {label}
      </div>
      <div className="mt-1 text-sm font-semibold" style={{ color: ui.textPrimary }}>
        {value}
      </div>
      {subtext ? (
        <div className="mt-1 text-[11px]" style={secondaryText}>
          {subtext}
        </div>
      ) : null}
    </div>
  );
}

// toneFromMlBucket removed 2026-08-11 — its only caller was the Deployment
// Interpretation card's ML badge.

/** Phase 2 Context card tile: label, value, optional caption, and the figure's OWN as-of (never implied). */
export function ContextTile({
  label,
  value,
  sub,
  asOf,
  testId,
}: {
  label: string;
  value: string;
  sub?: string | null;
  asOf: string;
  testId?: string;
}) {
  return (
    <div className="rounded-lg border px-3 py-2" style={{ borderColor: ui.cardBorder }} data-testid="screener-context-tile">
      <div className="text-xs" style={mutedText}>
        {label}
      </div>
      <div className="mt-1 font-semibold" data-testid={testId}>
        {value}
      </div>
      {sub ? (
        <div className="text-[11px] leading-4" style={mutedText}>
          {sub}
        </div>
      ) : null}
      <div className="mt-1 text-[10px]" style={mutedText} data-testid="screener-context-tile-asof">
        {asOf}
      </div>
    </div>
  );
}

// Cards nested inside the diagnostics drawer: same look, no second shadow.
export const subCardStyle: React.CSSProperties = { ...cardStyle, boxShadow: "none" };
